<script lang="ts">
    import { tick } from 'svelte'
    import type { Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { isEqual, orderBy } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { subaccount } from 'wdom/subaccount'
    import { recordSortArray } from 'wdom/zone/zone'
    import type { Zone, ZoneDomainRelation, ZoneStatus, ZoneTemplate } from 'wdom/zone/zone'

    interface Warning {
        key: string
        line: number | undefined
        entry: string
        message: string
    }

    interface Props {
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        zone: Writable<Zone | undefined>
        busy: Writable<boolean | string>
        zoneEdit: Writable<Zone | undefined>
        zoneStatus: Writable<ZoneStatus | null>
        zoneDomainRelation: Writable<ZoneDomainRelation>
        recordErrors: Writable<{ [key: string]: string }>
        mode?: 'admin' | 'client'
        addRecord: () => void
        applyTemplateData: (template: ZoneTemplate) => void
        importZoneData: (data: any, warnings: Warning[]) => void
        saveZone: () => void
        publishZone: () => void
        unpublishZone: () => void
        deleteZone: (reason: string) => void
    }

    let { status, zone, busy, zoneEdit, zoneStatus, zoneDomainRelation, recordErrors, mode = 'client', addRecord, applyTemplateData, importZoneData, saveZone, publishZone, unpublishZone, deleteZone }: Props = $props()

    // #################################################  Template stuff

    let templateGroups: Array<{ type: string; templates: ZoneTemplate[] }> = $state([])
    let templatesReady = $state(false)
    let templatesOpen = $state(false)

    async function loadTemplates() {
        try {
            const r = await simpleFetch('GET', '/json/zone/1/zone-templates', { include_global: '1' })
            for (const item of r.data.items) {
                item.mtime = toLuxon(item.mtime)
            }
            let tmpls: ZoneTemplate[] = r.data.items
            for (const tmpl of tmpls) {
                if (!tmpl.name && tmpl[`name_${$lg}`]) tmpl.name = tmpl[`name_${$lg}`]
                tmpl.type = tmpl.user_id != null ? 'user' : tmpl.public ? 'global' : 'admin'
            }
            tmpls = orderBy(tmpls, [(tmpl) => (tmpl.type == 'global' ? 0 : tmpl.type == 'admin' ? 1 : 2), (tmpl) => tmpl.name])
            const groupIndex = {}
            for (const tmpl of tmpls) {
                if (groupIndex[tmpl.type] == null) groupIndex[tmpl.type] = { type: tmpl.type, templates: [] }
                groupIndex[tmpl.type].templates.push(tmpl)

                tmpl.records = orderBy(tmpl.records, [(v) => recordSortArray(null, v)])
            }
            templateGroups = Object.values(groupIndex)
        } catch {}
        templatesReady = true
    }

    let ownerName: string | undefined = $state()
    async function fetchOwnerName() {
        if (!$zone) return
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${$zone.owner}/name`)
            ownerName = r.data.name
        } catch (e: any) {
            ownerName = '?'
        }
    }

    // #################################################  View as text stuff
    let copyTimer: ReturnType<typeof setTimeout> | undefined = undefined
    let copied: boolean | null = $state(null)

    function copyToClipboard() {
        if (showText == null) return
        copied = null
        navigator.clipboard.writeText(showText).then(
            () => {
                notify({
                    type: 'success',
                    title: $_('View zone as text'),
                    text: $_('The text has been copied to the clipboard.'),
                })
                copied = true
            },
            () => {
                notify({
                    type: 'danger',
                    title: $_('View zone as text'),
                    text: $_('Could not copy the text.'),
                })
                copied = false
            },
        )
    }

    let loadingText = $state(false)
    let showText: string | null = $state(null)
    let errorsText: { line: number | undefined; message: string }[] = $state([])
    async function loadText() {
        if (!$zoneEdit) return
        loadingText = true
        errorsText = []
        const data = {
            check_zone: true,
            idna: $zoneEdit.idna,
            data: {
                default_ttl: $zoneEdit.default_ttl,
                minimum_ttl: $zoneEdit.minimum_ttl,
                revision: $zoneEdit.revision,
            },
            records: $zoneEdit.records,
        }
        try {
            let r = await simpleFetch('POST', '/json/zone/1/display-as-text', data)

            showText = r.data.zone
        } catch (e: any) {
            if (e.status == 400 && e.data.zone) {
                showText = e.data.zone
                if (e.data.stderr) {
                    const lines = e.data.stderr.split('\n')
                    for (const line of lines) {
                        const m = line.match(/^\[.*?\] nsd-checkzone.*?error: .*?:(?<line>\d+): (?<msg>.*)$/)
                        if (m) {
                            errorsText.push({
                                line: m.groups['line'],
                                message: m.groups['msg'],
                            })
                        }
                    }
                } else {
                    errorsText.push({ line: undefined, message: $_('Unknown error') })
                }
            } else {
                notify({
                    type: 'danger',
                    title: $_('View zone as text'),
                    text: $_('There was an error processing the zone data.'),
                })
                console.error('Error loading zone as text:', e && e.data ? e.data : e)
            }
        }
        loadingText = false
    }

    // #################################################  Import text

    let importingText = $state(false)
    let showImport: boolean = $state(false)
    let zoneImportText = $state(null) // The text to import
    let zoneImportElement: HTMLElement | undefined = $state()
    let importErrors: { key: string; message: string; line: number | undefined; entry: string }[] | null = $state(null)
    async function importText() {
        if (!$zoneEdit) return

        importingText = true
        showImport = false

        try {
            let r = await simpleFetch('POST', '/json/zone/1/zone/import-from-text', { idna: $zoneEdit.idna, text: zoneImportText })
            const warnings: Warning[] = []
            if (r.data.errors && r.data.errors.length) {
                for (const error of r.data.errors) {
                    const w = parseZoneError(error)
                    if (w) warnings.push(w)
                }
            }
            importZoneData(r.data, warnings)
        } catch (e: any) {
            importErrors = []
            if (e && e.data)
                for (const error of e.data.errors) {
                    const w = parseZoneError(error)
                    if (w) importErrors.push(w)
                }
            showImport = true
        }
        importingText = false
    }

    function parseZoneError(error: string): { key: string; line: number | undefined; entry: string; message: string } | undefined {
        const match = error.match(/^(\([0-9:]+\))? *(\[.+?\])? *(.*)$/)
        if (!match) {
            console.error('Could not parse error message, ', error)
            return undefined
        }
        let line: number | undefined
        if (match[1] != null) {
            line = parseInt(match[1].substring(1, match[1].length - 1).split(':')[0])
        }
        let entryName: string | undefined
        if (match[2] != null) {
            entryName = match[2].substring(1, match[2].length - 1)
        }

        let key: string
        if (line != null && entryName) key = $_('Line {line_no}, “{entry}”', { line_no: line, entry: entryName })
        else if (line != null) key = $_('Line {line_no}', { line_no: line })
        else if (entryName) key = $_('“{entry}”', { entry: entryName })
        else key = '–'

        return {
            key: key,
            line: line,
            entry: entryName ? entryName : '',
            message: match[3],
        }
    }

    // #################################################  Delete zone

    let showDeleteConfirm = $state(false)
    let deleteReason: string = $state('')
    let cleanDeleteReason = $derived(deleteReason.trim())

    // Close any modal
    function closeAnything() {
        if (showText != null) {
            showText = null
        }
        if (showImport) {
            showImport = false
        }
        if (showDeleteConfirm) {
            showDeleteConfirm = false
        }
    }
    let unsavedChanges = $derived(!isEqual($zone, $zoneEdit))
    let hasErrors = $derived(Object.keys($recordErrors).length > 0)
    let nZoneEditRecords = $derived($zoneEdit && $zoneEdit.records ? $zoneEdit.records.length : 0)
    $effect(() => {
        if ($status == 'ready' && !templatesReady) loadTemplates()
    })
    $effect(() => {
        if (mode == 'admin' && $status == 'ready') fetchOwnerName()
    })
    $effect(() => {
        if (copied !== null) {
            if (copyTimer) clearTimeout(copyTimer)
            copyTimer = setTimeout(() => {
                copied = null
                copyTimer = undefined
            }, 3000)
        } else if (copyTimer) {
            clearTimeout(copyTimer)
            copyTimer = undefined
        }
    })
    // Keep the body unscrollable while a modal is open
    $effect(() => {
        if (showText != null || showImport || showDeleteConfirm) {
            document.documentElement.classList.add('modal-open')
            tick().then(() => {
                if (zoneImportElement) zoneImportElement.focus()
            })
        } else {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .dropdown-menu {
        /* To cover the underlying element, else looks weird */
        left: -1px;
        right: -1px;
    }
    .dd-menu-row.dd-mr-item:hover {
        background-color: #f5f5f5;
    }
    .dd-cat {
        padding: 3px 1.25rem;
        color: #aaa;
        font-size: 0.8em;
    }
    .dd-menu-row a.btn {
        padding-left: 1.5rem;
        padding-right: 1.5rem;

        text-align: left;
    }

    .progress {
        --bs-progress-height: 2.184375rem;
        --bs-progress-font-size: 0.873rem;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape') closeAnything()
    }}
/>
<svelte:body
    onclick={(e) => {
        if (templatesOpen) templatesOpen = false
    }}
/>

{#if $status == 'ready'}
    <div>
        <div class="standalone-title text-primary">{$_('Zone')}</div>
        <div class="card">
            <div class="card-body revision">
                <div class="d-grid gap-2">
                    {#if $status == 'ready' && $subaccount.zone}
                        {#if unsavedChanges}
                            <button type="button" disabled={!!$busy || hasErrors || !nZoneEditRecords} class="btn btn-primary" class:attention={!$busy && !hasErrors} onclick={() => saveZone()}>{$_('Save')}</button>
                        {:else if $zoneStatus && !$zoneStatus.published}
                            <button type="button" disabled={!!$busy || hasErrors} class="btn btn-primary" onclick={() => publishZone()}>{$_('Publish')}</button>
                            <button type="button" disabled={!!$busy || hasErrors} class="btn btn-warning" onclick={() => (showDeleteConfirm = true)}>{$_('Delete')}</button>
                        {:else if $zoneStatus}
                            <button type="button" disabled={!!$busy || hasErrors} class="btn btn-warning" onclick={() => unpublishZone()}>{$_('Unpublish')}</button>
                        {/if}
                    {/if}
                    {#if $busy == 'saving' || $busy == 'deleting'}
                        <div class="controls">
                            <div class="progress">
                                <div class="progress-bar" style="width: 100%">
                                    {#if $busy == 'saving'}
                                        <span>{$_('Saving…')}</span>
                                    {:else}
                                        <span>{$_('Removing…')}</span>
                                    {/if}
                                </div>
                            </div>
                        </div>
                    {/if}
                    <!-- “View as text” button -->
                    {#if $status == 'ready'}
                        {#if !loadingText}
                            <button type="button" class="btn btn-outline-secondary" onclick={() => loadText()} disabled={!$zoneEdit || !$zoneEdit.records.length}>{$_('View as text')}</button>
                        {:else}
                            <div class="progress">
                                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-label={$_('Loading…')} style="width:100%">{$_('Loading…')}</div>
                            </div>
                        {/if}
                    {/if}
                    <!-- “Import from text” button -->
                    {#if $status == 'ready' && $subaccount.zone}
                        {#if !importingText}
                            <button type="button" class="btn btn-outline-secondary" onclick={() => (showImport = true)}>{$_('Import from text')}</button>
                        {:else}
                            <div class="progress">
                                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-label={$_('Loading…')} style="width:100%">{$_('Loading…')}</div>
                            </div>
                        {/if}
                    {/if}
                    <!-- “Apply template” stuff -->
                    {#if $status == 'ready' && $subaccount.zone}
                        <div class="dropdown dropdown-block">
                            <button
                                class="btn btn-outline-secondary dropdown-toggle"
                                onclick={(e) => {
                                    e.stopPropagation()
                                    templatesOpen = !templatesOpen
                                }}
                                type="button"
                                id="zone-template-dd"
                                aria-haspopup="true"
                                aria-expanded="true"
                                disabled={!templatesReady || !!$busy}
                            >
                                <span class="text-center">{$_('Apply template')}</span>
                                <span class="caret"></span>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-right" class:show={templatesOpen} aria-labelledby="zone-template-dd">
                                {#each templateGroups as tgroup (tgroup.type)}
                                    <li class="dd-menu-row dd-mr-group"><span class="dd-cat">{tgroup.type == 'global' ? $_('System') : $_('User')}</span></li>
                                    {#each tgroup.templates as template (template.id)}
                                        <li class="dd-menu-row dd-mr-item">
                                            <a
                                                class="btn btn-link"
                                                href="/"
                                                onclick={(e) => {
                                                    console.log('Apply template: ', template)
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    applyTemplateData(template)
                                                    templatesOpen = false
                                                }}>{template.name}</a
                                            >
                                        </li>
                                    {/each}
                                {/each}
                            </ul>
                        </div>
                    {/if}
                    <!-- “Add a record” stuff -->
                    {#if $status == 'ready' && $subaccount.zone}
                        <button type="button" disabled={!!$busy} class="btn btn-outline-secondary" onclick={() => addRecord()}>{$_('Add a record')}</button>
                    {/if}
                    {#if $status == 'ready' && $zoneDomainRelation.domain_exists}
                        <a use:sanchor href={spath(`/domain/${$zoneDomainRelation.idna}`)} class="btn btn-outline-secondary">{$_('Go to the domain')}</a>
                    {/if}
                </div>
            </div>
        </div>
    </div>

    {#if mode == 'admin'}
        <div>
            <div class="standalone-title text-primary">{$_('Owner')}</div>
            <div class="card">
                <div class="card-body d-grid">
                    {#if ownerName == null}
                        <button type="button" class="btn btn-outline-secondary">
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            Chargement…
                        </button>
                    {:else if $zone}
                        <a use:sanchor href={spath(`/wdom-user/${$zone.owner}`)} class="btn btn-outline-secondary">{ownerName}</a>
                    {/if}
                </div>
            </div>
        </div>
    {/if}

    <!-- =============== Modals =============== -->
    <!-- “View as text” modal -->
    {#if showText != null}
        <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        {$_('Zone text')}
                    </div>
                    <div class="modal-body">
                        <pre class="zone-preview numbered"><div>
                            {#each showText.split('\n') as line}
                                    <span></span>
                                <code>{line}</code>
                                {/each}
                        </div></pre>
                        {#if errorsText.length}
                            <div class="alert alert-danger no-margin">
                                <ul style="margin-bottom: 0">
                                    {#each errorsText as error}
                                        {#if error.line}
                                            <li>{$_('Line {line}: {message}', { line: error.line, message: error.message })}</li>
                                        {:else}
                                            <li>{error.message}</li>
                                        {/if}
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                    </div>
                    <div class="modal-footer">
                        {#if navigator.clipboard.writeText != null}
                            {#if copied !== null}
                                <button type="button" transition:fade={{ duration: 250 }} class="btn" disabled={true} class:btn-success={copied} class:btn-danger={!copied}>
                                    {#if copied}{$_('Copied!')}{:else}{$_('Could not copy the text.')}{/if}
                                </button>
                            {/if}

                            <button class="btn btn-primary" type="button" onclick={() => copyToClipboard()}>{$_('Copy to clipboard')}</button>
                        {/if}
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (showText = null)}>{$_('Close')}</button>
                    </div>
                </div>
            </div>
            <div class="modal-backdrop" aria-hidden="true" onclick={() => (showText = null)}></div>
        </div>
    {/if}

    <!-- “Import from text” modal -->
    {#if showImport}
        <div class="modal" role="dialog" transition:fade={{ duration: 250 }} aria-hidden="true" onclick={() => (showImport = false)}>
            <div
                class="modal-dialog modal-lg"
                aria-hidden="true"
                onclick={(e) => {
                    e.stopPropagation()
                }}
            >
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        {$_('Import zone')}
                    </div>
                    <div class="modal-body">
                        <form
                            onsubmit={(e) => {
                                e.preventDefault()
                            }}
                            class="border-group"
                        >
                            <div class="form-group">
                                <label for="zoneImportText">{$_('Zone text')}</label>
                                <p class="help-block">{$_('Paste the text of the zone to import below.')}</p>
                                <textarea id="zoneImportText" class="form-control" style="white-space: pre; word-wrap: normal;" rows="8" bind:value={zoneImportText} bind:this={zoneImportElement}></textarea>
                            </div>
                        </form>
                        {#if importErrors != null && importErrors.length}
                            <div class="alert alert-danger no-margin">
                                <dl style="margin-bottom: 0">
                                    {#each importErrors as error}
                                        <dt>{error.key}</dt>
                                        <dd style="margin-left: 1em;">{error.message}</dd>
                                    {/each}
                                </dl>
                            </div>
                        {/if}
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (showImport = false)}>{$_('Cancel')}</button>
                        <button type="button" class="btn btn-primary" disabled={importingText || !zoneImportText} onclick={() => importText()}>{$_('Import')}</button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    {/if}

    <!-- “Confirm delete” modal -->
    {#if showDeleteConfirm}
        <div class="modal" role="dialog" transition:fade={{ duration: 250 }} aria-hidden="true" onclick={() => (showDeleteConfirm = false)}>
            <div
                class="modal-dialog"
                aria-hidden="true"
                onclick={(e) => {
                    e.stopPropagation()
                }}
            >
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        {$_('Zone removal')}
                    </div>
                    <div class="modal-body">
                        {$_('If you remove this zone, all its data will be lost.')}
                        {#if mode == 'admin'}
                            <textarea class="form-control" bind:value={deleteReason}></textarea>
                        {/if}
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (showDeleteConfirm = false)}>{$_('Cancel')}</button>
                        <button
                            type="button"
                            class="btn btn-danger"
                            onclick={() => {
                                showDeleteConfirm = false
                                deleteZone(cleanDeleteReason)
                            }}
                            >{$_('Delete the zone file')}{#if cleanDeleteReason != ''}{mode == 'admin' ? ' (+raison)' : ''}{/if}</button
                        >
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    {/if}
{/if}
